import { useSearchParams } from "react-router-dom";
import { useAction, useQuery } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { useAuth } from "../hooks/useAuth";
import { PaywallCard } from "../components/PaywallCard";
import { EmptyState } from "../components/ui/EmptyState";
import { LoadingState } from "../components/ui/LoadingState";
import type { PlanInfo } from "../domain/types";
import {
  getPendingPurchaseContext,
  getPurchaseTrackingContext,
  savePendingPurchaseContext,
} from "../lib/pendingPurchase";
import { withTimeout } from "../lib/withTimeout";
import { t } from "../i18n";
import { useEffect, useRef, useState } from "react";
import { getBrowserBillingCurrency } from "../lib/localizedPricing";
import { trackBrowserPurchase } from "../lib/analytics";

export function AppBillingPage() {
  const { session, entitlement, isLoading } = useAuth();
  const [searchParams] = useSearchParams();
  const targetHandle = searchParams.get("handle") ?? undefined;
  const [actionLoading, setActionLoading] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const createPurchaseLink = useAction(api.stripe.createWebCheckoutLink);
  const billingCurrency = getBrowserBillingCurrency();
  const createManagementLink = useAction(
    api.stripe.createWebBillingManagementLink,
  );
  const syncSubscriptionStatus = useAction(
    api.stripe.syncWebSubscriptionStatus,
  );
  const subscription = useQuery(
    api.monitors.getUserSubscriptionStatus,
    session ? {} : "skip",
  );
  const pendingPurchase = getPendingPurchaseContext();
  const purchaseTrackingContext = getPurchaseTrackingContext(
    typeof window !== "undefined" ? window.location.search : "",
    pendingPurchase,
  );
  const purchaseTrackedRef = useRef(false);

  useEffect(() => {
    if (
      purchaseTrackedRef.current ||
      !purchaseTrackingContext
    ) {
      return;
    }

    purchaseTrackedRef.current = true;
    trackBrowserPurchase(purchaseTrackingContext);
  }, [purchaseTrackingContext]);

  const handleSelectPlan = async (plan: PlanInfo) => {
    setActionLoading(true);
    setActionMessage(null);
    setActionError(null);
    try {
      await withTimeout(
        (async () => {
          const isWeekly = plan.period === "weekly";
          savePendingPurchaseContext({
            source: "app_renewal",
            handle: targetHandle,
          });

          const result = await createPurchaseLink({
            priceLookupKey: isWeekly ? "weekly" : "monthly",
            currency: billingCurrency,
            handle: targetHandle,
            returnTo: "billing",
          });

          window.location.assign(result.url);
        })(),
      );
    } catch (err) {
      console.error("Failed to create checkout session", err);
      setActionError(t("billingCheckoutError"));
    } finally {
      setActionLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    setActionLoading(true);
    setActionMessage(null);
    setActionError(null);
    try {
      await withTimeout(
        (async () => {
          const result = await createManagementLink({
            returnUrl: `${window.location.pathname}${window.location.search}`,
          });
          window.location.assign(result.url);
        })(),
      );
    } catch (err) {
      console.error("Failed to open customer portal", err);
      setActionError(t("billingPortalError"));
    } finally {
      setActionLoading(false);
    }
  };

  const handleRestorePurchase = async () => {
    setRestoreLoading(true);
    setActionMessage(null);
    setActionError(null);
    try {
      await withTimeout(
        syncSubscriptionStatus({}).then((result) => {
          setActionMessage(
            result.isPremium
              ? t("paywallRestoreSuccess")
              : t("paywallRestoreNotFound"),
          );
        }),
      );
    } catch (err) {
      console.error("Failed to restore purchase", err);
      setActionError(t("paywallRestoreError"));
    } finally {
      setRestoreLoading(false);
    }
  };

  if (isLoading || (session && subscription === undefined)) {
    return <LoadingState title={t("commonLoading")} />;
  }

  const getFriendlyPlanLabel = (key?: string) => {
    if (!key) return "Premium";
    const k = key.toLowerCase();
    if (k.includes("weekly") || k.includes("_1w") || k.endsWith("weekly")) {
      return t("paywallPlanWeekly");
    }
    if (k.includes("monthly") || k.includes("_1m") || k.endsWith("monthly")) {
      return t("paywallPlanMonthly");
    }
    return key;
  };

  const hasPremium = subscription?.isPremium ?? entitlement?.isPremium ?? false;
  const canManageSubscription =
    hasPremium &&
    (subscription?.activeProvider === "stripe" ||
      subscription?.activeProvider === "revenuecat");
  const planLimit = subscription?.planLimit ?? entitlement?.planLimit ?? 0;
  const planLabel = hasPremium
    ? subscription?.promoCode
      ? `Promo ${subscription.promoCode}`
      : getFriendlyPlanLabel(entitlement?.planKey)
    : t("billingFree");
  const statusLabel = hasPremium
    ? t("statusActive")
    : t("billingNoSubscription");
  const expiryLabel = subscription?.expiresAt
    ? new Date(subscription.expiresAt).toLocaleDateString()
    : null;
  const accountStatus = statusLabel;

  return (
    <div className="app-page app-billing-page">
      <div className="app-page-header">
        <div>
          <h1>{hasPremium ? t("billingTitle") : t("renewalTitle")}</h1>
          {!hasPremium && (
            <p className="app-page-subtitle">
              {targetHandle
                ? t("billingRenewBodyTarget", { handle: targetHandle })
                : t("billingRenewBody")}
            </p>
          )}
        </div>
      </div>

      {!hasPremium ? (
        <section className="app-billing-renewal-shell">
          <div className="app-billing-renewal-copy">
            <p className="app-eyebrow">{t("billingRenewKicker")}</p>
            <h2>
              {targetHandle
                ? t("billingRenewTitleTarget", { handle: targetHandle })
                : t("billingRenewTitle")}
            </h2>
            <p>
              {targetHandle
                ? t("billingRenewIntroTarget", { handle: targetHandle })
                : t("billingRenewIntro")}
            </p>
            <div className="app-billing-current-plan">
              <div>
                <span>{t("billingCurrentPlan")}</span>
                <strong>{planLabel}</strong>
              </div>
              <div>
                <span>
                  {t("dashboardAccountLimit", { current: 0, limit: planLimit })}
                </span>
                <strong>{accountStatus}</strong>
              </div>
            </div>
          </div>

          <PaywallCard
            targetHandle={targetHandle}
            onSelectPlan={handleSelectPlan}
            onRestorePurchase={handleRestorePurchase}
            isLoading={actionLoading}
            isRestoring={restoreLoading}
          />
          {actionMessage && (
            <p className="app-settings-note">{actionMessage}</p>
          )}
          {actionError && <p className="app-settings-error">{actionError}</p>}
        </section>
      ) : (
        entitlement && (
          <section className="app-settings-card app-billing-active-card">
            <div className="app-settings-card-head">
              <div>
                <p className="app-eyebrow">{t("billingCurrentPlan")}</p>
                <h2>{planLabel}</h2>
                <span>
                  {t("dashboardAccountLimit", { current: 0, limit: planLimit })}
                </span>
              </div>
              <span className="app-status-pill app-status-success">
                <span />
                {t("statusActive")}
              </span>
            </div>

            {expiryLabel && (
              <p className="app-settings-note">
                {t("billingExpiresAt", { date: expiryLabel })}
              </p>
            )}
            {actionError && <p className="app-settings-error">{actionError}</p>}

            {canManageSubscription && (
              <button
                className="app-secondary-button"
                onClick={handleManageSubscription}
                disabled={actionLoading}
              >
                {actionLoading ? t("commonLoading") : t("billingManage")}
              </button>
            )}
          </section>
        )
      )}

      {!session && (
        <div className="mt-8">
          <EmptyState
            title={t("billingSignInTitle")}
            body={t("billingSignInBody")}
          />
        </div>
      )}
    </div>
  );
}
